import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import ClinicCard from "@/components/ClinicCard";
import NationalContextSidebar from "@/components/NationalContextSidebar";
import Rubric from "@/components/Rubric";
import { SITE_URL } from "@/lib/site";
import { itemListSchema, articleSchema } from "@/components/schema";
import { MADRID_CLINICS } from "@/data/clinics-madrid";
import { Helmet } from "react-helmet-async";

const PATH = "/best-veneer-clinics-in-madrid";
const TITLE = "Porcelain veneer clinics in Madrid — an evaluation";
const DESCRIPTION =
  "Five Madrid cosmetic-dentistry practices examined on the same twelve-dimension rubric. Each clinic linked to its own website.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function BestVeneerClinicsMadrid() {
  const itemList = itemListSchema(
    MADRID_CLINICS.map((c) => ({
      name: c.name,
      url: `${SITE_URL}${PATH}#${c.slug}`,
      description: c.profile,
    })),
    PATH,
  );

  const article = articleSchema({
    title: TITLE,
    description: DESCRIPTION,
    path: PATH,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
  });

  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Spain", path: "/best-smile-makeover-clinics-in-spain" },
          { name: "Madrid", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Comparison &middot; Madrid"
        title="Porcelain veneer clinics in Madrid — an evaluation"
        standfirst="Five Madrid cosmetic-dentistry practices examined on a single rubric, in the same order, linked to their own websites. The capital's range runs from long-tenure multi-site groups to single-clinician Gran Vía studios — comparing them without the rubric is comparing different categories."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={16}
      />

      <div className="container-prose prose-editorial mt-10">
        <h2>Why Madrid, as a category</h2>
        <p>
          Madrid is the largest cosmetic-dentistry market in Spain and
          the one with the highest structural range. A capital city
          with both a long-resident aesthetic-medicine clientele in
          Salamanca and Chamberí, a young professional cohort, and a
          non-trivial flow of international patients arriving through
          Barajas. The result is a market that supports four quite
          different clinic models at once: long-tenure multi-specialty
          groups, single-clinician studio practices, family
          general-practices with in-house labs, and dental-tourism-
          oriented aesthetic operations. A reader comparing two Madrid
          clinics is often comparing two different categories.
        </p>
        <p>
          This piece examines five practices across that range. Each is
          evaluated on the same rubric, in the same order, with the
          same reserve.
        </p>

        <h2>The rubric, applied locally</h2>
        <p>
          The full rubric lives on our{" "}
          <Link to="/methodology">methodology page</Link>. Summarised:
        </p>
        <Rubric variant="summary" />
        <p className="mt-6">
          For Madrid specifically, the dimensions that differentiate
          most sharply are lab model and ceramist proximity,
          preparation philosophy, and the authorial versus operational
          positioning of the clinic. On international-patient handling,
          most serious Madrid practices are competent — the market
          supports it.
        </p>

        <h2>The clinics evaluated</h2>
        <p>
          Profiles below follow the same structure: a short description
          of the practice in its own terms, a dimension-by-dimension
          snapshot, and an honest statement of where the clinic leads
          on this list and where the rubric places it behind peers.
          Every external link goes to the clinic's own website.
        </p>

        {MADRID_CLINICS.map((clinic, i) => (
          <ClinicCard key={clinic.slug} clinic={clinic} position={i + 1} />
        ))}

        <h2>What the framework surfaces, on balance</h2>
        <p>
          Across the five practices examined, three patterns stand out.
        </p>
        <p>
          The <strong>in-house lab</strong> dimension, which separates
          boutique studios from partner-lab aesthetic operations, is
          present on this list — and not where a reader might expect.
          The clinic with the most visible in-clinic prosthetics
          laboratory is the Majadahonda family practice, not the
          central-Madrid aesthetic studios. This is worth noting: lab
          proximity does not always correlate with central-city
          marketing reach.
        </p>
        <p>
          The <strong>authorial smile-design</strong> positioning — the
          language of a designed smile rather than a placed veneer — is
          clearest at the smaller Salamanca-district and Gran Vía
          studios. Larger multi-site groups talk about aesthetic
          dentistry as one service line among many; smaller studios
          talk about it as the defining identity.
        </p>
        <p>
          On <strong>operational scale and international-patient
          handling</strong>, the two-site groups and the central-
          tourist-district clinics lead. A reader whose priority is an
          English-first intake and flexible scheduling will find that
          answer faster at the larger operations than at the single-
          clinician studios.
        </p>
        <p>
          The point of the piece is not a ranking. It is that the
          reader now knows which dimensions to weight for themselves.
        </p>

        <NationalContextSidebar currentCity="Madrid" />

        <h2>Related reading</h2>
        <p>
          Readers comparing Madrid clinics to clinics in other Spanish
          cities should consult the{" "}
          <Link to="/best-smile-makeover-clinics-in-spain">
            national smile-makeover guide
          </Link>
          , which applies this same rubric across Madrid, Barcelona,
          Valencia, Seville and the Costa del Sol. If your search also
          includes Barcelona, see our{" "}
          <Link to="/best-veneer-clinics-in-barcelona">
            Barcelona clinic evaluation
          </Link>
          . The vocabulary used in the profiles above is developed in{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          {" "}and{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          .
        </p>

        <hr className="rule" />

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>
              2026-04-18 — First publication. Each clinic's name, site
              and public positioning confirmed against the clinic's own
              website at the time of writing. Entries marked{" "}
              <code>verified: false</code> in the source data await
              editor confirmation of current operational status.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
